import { Cloudinary } from "@cloudinary/url-gen"
import { Resize } from "@cloudinary/url-gen/actions/resize"
import { useMemo } from "react"
import { Sliders, SliderType, TTeaser } from "src/components/Sliders/Slider"
import {
  PublicationLevel,
  SearchAlbumsResult,
  SearchArtistsResult,
  SearchByFacetValueQuery,
  SearchByTextQuery,
  SearchEditorialPlaylistsResult,
  SearchGroupsResult,
  SearchLiveConcertsResult,
  SearchPartnersResult,
  SearchVideosResult,
  SearchVodConcertsResult,
} from "src/generated/graphql"
import useIntl from "src/hooks/use-intl"
import useTranslate, { Namespaces } from "src/hooks/use-translate"
import { mapTeaserDimensions } from "src/screens/Detail/DetailScreen"
import { appStore } from "src/store/AppStore"
import { PlaybackContentType, TranslationKey } from "src/types"
import {
  ccFactor,
  CloudinaryConf,
  secondsToMinutes,
} from "src/utilities/common"
import { isContentAvailable } from "src/utilities/content-availability-helpers"

import { s } from "../utilities/common"
import { maxSliderSize } from "../utilities/constants"

const mapAbstractTypes: Record<
  string,
  | PlaybackContentType
  | "Artist"
  | "Partner"
  | "Group"
  | "Epoch"
  | "Genre"
  | "EditorialPlaylist"
> = {
  SearchVideo: "Video",
  SearchAlbum: "Album",
  SearchEditorialPlaylist: "EditorialPlaylist",
  SearchVodConcert: "VodConcert",
  SearchLiveConcert: "LiveConcert",
  SearchArtist: "Artist",
  SearchPartner: "Partner",
  SearchGroup: "Group",
}

export type SearchResultNode = keyof Extract<
  SearchByTextQuery["searchByText"],
  { __typename: "SearchByTextResult" }
>
export const nodes: Partial<Record<SearchResultNode, SearchResultNode>> = {
  albums: "albums",
  documentaries: "documentaries",
  shorts: "shorts",
  artists: "artists",
  groups: "groups",
  interviews: "interviews",
  liveConcerts: "liveConcerts",
  partners: "partners",
  vodConcerts: "vodConcerts",
  editorialPlaylists: "editorialPlaylists",
}

const cld = new Cloudinary(CloudinaryConf)

export const useSearchSliders = (
  input?: SearchByFacetValueQuery | SearchByTextQuery,
  maxSliderSizeOverride?: number,
) => {
  const { dateFormat } = useIntl()
  const t = useTranslate()
  const geoInfo = appStore((state) => state.geoInfo)

  return useMemo((): Sliders | undefined => {
    if (!input) return undefined
    const data =
      "searchByFacetValue" in input
        ? input.searchByFacetValue
        : "searchByText" in input
          ? input.searchByText
          : undefined
    if (!data) return undefined
    return (
      [
        [
          {
            ...data.groups,
            items: [...data.groups.items, ...data.artists.items].slice(
              0,
              maxSliderSizeOverride ?? 9999,
            ),
          },
          "search__results_slider_artists",
          nodes.artists,
        ],
        [data.vodConcerts, "search__results_slider_archive", nodes.vodConcerts],
        [data.albums, "search__results_slider_audio", nodes.albums],
        [
          data.documentaries,
          "search__results_slider_documentaries",
          nodes.documentaries,
        ],
        [
          data.interviews,
          "search__results_slider_interviews",
          nodes.interviews,
        ],
        [data.shorts, "search__results_slider_shorts", nodes.shorts],
        [
          data.liveConcerts,
          "search__results_slider_upcoming_live",
          nodes.liveConcerts,
        ],
        [data.partners, "search__results_slider_partners", nodes.partners],
        // FIXME this TL key doesn't exist yet
        [
          data.editorialPlaylists,
          "search__results_slider_playlists",
          nodes.editorialPlaylists,
        ],
      ] as [
        (
          | SearchArtistsResult
          | SearchVideosResult
          | SearchVodConcertsResult
          | SearchAlbumsResult
          | SearchGroupsResult
          | SearchPartnersResult
          | SearchLiveConcertsResult
          | SearchEditorialPlaylistsResult
        ),
        TranslationKey<Namespaces.Default>,
        SearchResultNode,
      ][]
    )
      .map((slidersAndTranslations) => {
        const slider = slidersAndTranslations[0]
        const items = slider.items
        if (!items || items.length === 0) return undefined
        return {
          id: items[0].__typename as string,
          title: t(slidersAndTranslations[1]) as string,
          type: SliderType.Vertical,
          style: mapTeaserDimensions(mapAbstractTypes[items[0].__typename]),
          node: slidersAndTranslations[2],
          pageInfo: { hasNextPage: slider.totalResults > maxSliderSize },
          totalResults: slider.totalResults,
          teasers: items
            // remove dupes
            .filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.id === value.id),
            )
            .filter((edge) => isContentAvailable(edge, geoInfo))
            .map((edge) => ({
              id: edge.id,
              uniqueId: edge.id,
              type: mapAbstractTypes[edge.__typename],
              title:
                edge.__typename === "SearchVideo" ||
                edge.__typename === "SearchVodConcert" ||
                edge.__typename === "SearchLiveConcert" ||
                edge.__typename === "SearchAlbum" ||
                edge.__typename === "SearchEditorialPlaylist"
                  ? edge.title
                  : edge.name,
              subtitle:
                edge.__typename === "SearchVideo" ||
                edge.__typename === "SearchVodConcert" ||
                edge.__typename === "SearchLiveConcert"
                  ? edge.subtitle
                  : edge.__typename === "SearchAlbum"
                    ? edge.artistsAndGroups
                    : edge.__typename === "SearchArtist"
                      ? edge.roles.join(", ")
                      : edge.__typename === "SearchGroup"
                        ? edge.subtype
                        : undefined,
              caption:
                edge.__typename === "SearchLiveConcert" && edge.startDate
                  ? dateFormat(new Date(edge.startDate), "shortTime")
                  : edge.__typename === "SearchVideo" ||
                      edge.__typename === "SearchVodConcert"
                    ? t("duration__in_minutes", [
                        secondsToMinutes(edge.duration),
                      ])
                    : undefined,
              label:
                edge?.__typename === "SearchVideo" ||
                edge?.__typename === "SearchVodConcert"
                  ? edge.typeDisplayName
                  : edge?.__typename === "SearchEditorialPlaylist"
                    ? edge.typeDisplayName
                    : undefined,
              publicationLevel:
                edge.__typename === "SearchVideo"
                  ? edge.publicationLevel
                  : PublicationLevel.TicketRequired,
              liveConcertType: undefined,
              picture:
                edge.__typename === "SearchVideo" ||
                edge.__typename === "SearchVodConcert" ||
                edge.__typename === "SearchLiveConcert" ||
                edge.__typename === "SearchArtist" ||
                edge.__typename === "SearchGroup" ||
                edge.__typename === "SearchAlbum" ||
                edge.__typename === "SearchPartner" ||
                edge.__typename === "SearchEditorialPlaylist"
                  ? cld
                      .image(
                        edge.images.filter((i) => {
                          if (!i) return false
                          return (
                            i.type ===
                            (edge.__typename === "SearchAlbum" ||
                            edge.__typename === "SearchEditorialPlaylist"
                              ? "COVER"
                              : edge.__typename === "SearchPartner"
                                ? "LOGO"
                                : "TEASER")
                          )
                        })[0]?.url,
                      )
                      .resize(Resize.scale().width(s(ccFactor(587), true)))
                      .format("jpg")
                      .setDeliveryType("fetch")
                      .toURL()
                  : undefined,
            })) as TTeaser[],
        }
      })
      .filter((slider) => slider !== undefined)
  }, [input, dateFormat, t, geoInfo])
}
